import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, ArrowRight, BarChart3, Building2, Database } from 'lucide-react';
import api from '../../lib/api';
import Button from '../../components/Button';
import AdminPageShell from '../components/AdminPageShell';
import AdminStatCard from '../components/AdminStatCard';
import { ADMIN_TOOL_GROUPS, adminToolPath, AdminToolStatus } from '../registry';
import { PeopleIcon } from '../../components/icons';

interface AdminStats {
  totalUsers: number;
  totalOrganizations: number;
  totalWorkspaces: number;
  totalBills: number;
}

const statusChip: Record<AdminToolStatus, { label: string; className: string }> = {
  ready: {
    label: 'Rebuilt',
    className:
      'bg-success-100 text-success-800 dark:bg-success-900 dark:bg-opacity-40 dark:text-success-300',
  },
  'in-progress': {
    label: 'In progress',
    className:
      'bg-primary-100 text-primary-800 dark:bg-primary-900 dark:bg-opacity-40 dark:text-primary-300',
  },
  legacy: {
    label: 'Legacy',
    className:
      'bg-warning-100 text-warning-800 dark:bg-warning-900 dark:bg-opacity-40 dark:text-warning-300',
  },
};

const AdminDashboardPage: React.FC = () => {
  const stats = useQuery({
    queryKey: ['admin-stats'],
    queryFn: async () => {
      const response = await api.get('/admin/stats');
      return response.data as AdminStats;
    },
  });

  return (
    <AdminPageShell
      title="Admin Dashboard"
      description="Manage platform accounts, civic data, communications, and imports"
    >
      {stats.isError && (
        <div className="card flex items-center gap-3 border-l-4 border-error-500 p-4">
          <AlertTriangle className="h-5 w-5 shrink-0 text-error-600 dark:text-error-400" />
          <p className="flex-1 text-sm text-text-secondary dark:text-text-dark-secondary">
            Couldn't load platform stats. This may be temporary.
          </p>
          <Button variant="outline" size="sm" onClick={() => stats.refetch()}>
            Retry
          </Button>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4 xl:grid-cols-4">
        <AdminStatCard
          label="Total Users"
          value={stats.data?.totalUsers}
          icon={PeopleIcon}
          loading={stats.isLoading}
        />
        <AdminStatCard
          label="Organizations"
          value={stats.data?.totalOrganizations}
          icon={Building2}
          loading={stats.isLoading}
        />
        <AdminStatCard
          label="Workspaces"
          value={stats.data?.totalWorkspaces}
          icon={BarChart3}
          loading={stats.isLoading}
        />
        <AdminStatCard
          label="Bills"
          value={stats.data?.totalBills}
          icon={Database}
          loading={stats.isLoading}
        />
      </div>

      {ADMIN_TOOL_GROUPS.map((group) => (
        <div key={group.key}>
          <h3 className="mb-2 mt-6 text-sm font-semibold uppercase tracking-wide text-text-tertiary dark:text-text-dark-tertiary">
            {group.title}
          </h3>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {group.tools.map((tool) => (
              <Link
                key={tool.key}
                to={adminToolPath(tool)}
                className="card block p-5 transition-shadow hover:shadow-lg"
              >
                <div className="flex items-center gap-2">
                  <tool.icon className="h-5 w-5 text-primary-700 dark:text-primary-400" />
                  <span className="flex-1 text-base font-semibold text-text-primary dark:text-text-dark-primary">
                    {tool.title}
                  </span>
                  <span
                    className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusChip[tool.status].className}`}
                  >
                    {statusChip[tool.status].label}
                  </span>
                </div>
                <p className="mt-2 text-sm text-text-secondary dark:text-text-dark-secondary">
                  {tool.description}
                </p>
                <div className="mt-3 flex items-center gap-1 text-sm font-medium text-primary-700 dark:text-primary-400">
                  Open {tool.title}
                  <ArrowRight className="h-4 w-4" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      ))}

      <p className="pt-4 text-sm text-text-tertiary dark:text-text-dark-tertiary">
        Looking for a tool that isn't rebuilt yet? The{' '}
        <Link to="/admin" className="text-primary-700 underline dark:text-primary-400">
          legacy admin panel
        </Link>{' '}
        remains available during the transition.
      </p>
    </AdminPageShell>
  );
};

export default AdminDashboardPage;
